/**
 * WeaveIn.ai Platform - Analytics Cache Utility
 * Simple in-memory cache with TTL for analytics queries
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

class AnalyticsCache {
  private cache: Map<string, CacheEntry<any>> = new Map();
  private defaultTTL: number = 5 * 60 * 1000; // 5 minutes

  /**
   * Generate cache key from query parameters
   */
  private generateKey(endpoint: string, params: Record<string, any>): string {
    const sortedParams = Object.keys(params)
      .sort()
      .map(key => `${key}=${params[key]}`)
      .join('&');
    return `${endpoint}?${sortedParams}`;
  }

  /**
   * Check if cache entry is still valid
   */
  private isValid<T>(entry: CacheEntry<T>): boolean {
    return Date.now() - entry.timestamp < entry.ttl;
  }

  /**
   * Get cached data if available and valid
   */
  get<T>(endpoint: string, params: Record<string, any> = {}): T | null {
    const key = this.generateKey(endpoint, params);
    const entry = this.cache.get(key);

    if (!entry) {
      return null;
    }

    if (!this.isValid(entry)) {
      this.cache.delete(key);
      return null;
    }

    console.log(`[Analytics Cache] HIT: ${key}`);
    return entry.data as T;
  }

  /**
   * Store data in cache
   */
  set<T>(
    endpoint: string,
    params: Record<string, any> = {},
    data: T,
    ttl: number = this.defaultTTL
  ): void {
    const key = this.generateKey(endpoint, params);
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl,
    });
    console.log(`[Analytics Cache] SET: ${key} (TTL: ${ttl}ms)`);
  }

  /**
   * Invalidate specific cache entry
   */
  invalidate(endpoint: string, params: Record<string, any> = {}): void {
    const key = this.generateKey(endpoint, params);
    this.cache.delete(key);
    console.log(`[Analytics Cache] INVALIDATED: ${key}`);
  }

  /**
   * Invalidate all cache entries matching a pattern
   */
  invalidatePattern(pattern: string): void {
    const keys = Array.from(this.cache.keys());
    let count = 0;

    for (const key of keys) {
      if (key.includes(pattern)) {
        this.cache.delete(key);
        count++;
      }
    }

    console.log(`[Analytics Cache] INVALIDATED ${count} entries matching: ${pattern}`);
  }

  /**
   * Clear all cache entries
   */
  clear(): void {
    const size = this.cache.size;
    this.cache.clear();
    console.log(`[Analytics Cache] CLEARED ${size} entries`);
  }

  /**
   * Clean up expired entries
   */
  cleanup(): void {
    const keys = Array.from(this.cache.keys());
    let removed = 0;

    for (const key of keys) {
      const entry = this.cache.get(key);
      if (entry && !this.isValid(entry)) {
        this.cache.delete(key);
        removed++;
      }
    }

    if (removed > 0) {
      console.log(`[Analytics Cache] CLEANUP: Removed ${removed} expired entries`);
    }
  }

  /**
   * Get cache statistics
   */
  getStats(): {
    size: number;
    entries: Array<{ key: string; age: number; ttl: number }>;
  } {
    const entries = Array.from(this.cache.entries()).map(([key, entry]) => ({
      key,
      age: Date.now() - entry.timestamp,
      ttl: entry.ttl,
    }));

    return {
      size: this.cache.size,
      entries,
    };
  }
}

// Singleton instance
const analyticsCache = new AnalyticsCache();

// Auto-cleanup every 10 minutes
setInterval(() => {
  analyticsCache.cleanup();
}, 10 * 60 * 1000);

/**
 * Fetch with cache wrapper
 */
export const fetchWithCache = async <T>(
  url: string,
  options: RequestInit = {},
  cacheOptions: { ttl?: number; skipCache?: boolean } = {}
): Promise<T> => {
  const { ttl, skipCache = false } = cacheOptions;

  // Extract endpoint and params from URL
  const urlObj = new URL(url);
  const endpoint = urlObj.pathname;
  const params: Record<string, any> = {};
  urlObj.searchParams.forEach((value, key) => {
    params[key] = value;
  });

  // Check cache first
  if (!skipCache) {
    const cached = analyticsCache.get<T>(endpoint, params);
    if (cached !== null) {
      return cached;
    }
  }

  // Fetch from API
  console.log(`[Analytics Cache] MISS: ${endpoint}`);
  const response = await fetch(url, options);

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const data: T = await response.json();

  // Store in cache
  if (!skipCache) {
    analyticsCache.set(endpoint, params, data, ttl);
  }

  return data;
};

/**
 * Invalidate cache when forms are modified
 */
export const invalidateFormsCache = (): void => {
  analyticsCache.invalidatePattern('/forms/analytics');
  analyticsCache.invalidatePattern('/forms/instances');
};

/**
 * Invalidate cache for specific member
 */
export const invalidateMemberFormsCache = (memberId: number): void => {
  analyticsCache.invalidatePattern(`/forms/instances/by-member/${memberId}`);
};

export default analyticsCache;
